// src/pages/team/TeamRoleDetailPage.tsx

import { useEffect, useState, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useTeam, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Alert,
  Badge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  ErrorAlert,
} from '@react/components';
import { getTeamName } from '@/features/team/logic';
import { RoleFormDialog, type RoleFormValue } from '@/features/team/RoleFormDialog';
import { teamApi } from '@core/api';

export const TeamRoleDetailPage = observer(() => {
  const { id, roleId } = useParams<{ id: string; roleId: string }>();
  const navigate = useNavigate();
  const store = useTeam();
  const { t } = useI18n();

  const [role, setRole] = useState<any>(null);
  const [permissions, setPermissions] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [showEdit, setShowEdit] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [confirmClearPerms, setConfirmClearPerms] = useState(false);
  const [clearingPerms, setClearingPerms] = useState(false);
  const [canManageRoles, setCanManageRoles] = useState(false);

  useEffect(() => {
    if (!id) return;
    let alive = true;
    setCanManageRoles(false);
    teamApi.GetTeamDetails(id).then((res) => {
      if (alive) setCanManageRoles(Boolean(res.permissions?.can_create_roles));
    }).catch(() => { if (alive) setCanManageRoles(false); });
    return () => { alive = false; };
  }, [id]);

  const loadRole = useCallback(async () => {
    if (!id || !roleId) return;
    try {
      const res = await store.ListTeamRoles(id);
      const found = (res.roles?.roles ?? []).find(
        (r: any) => (r.entity?.id || r.id) === roleId
      );
      if (found) {
        setRole(found);
        setPermissions(found.permissions?.permissions || []);
      }
    } catch { /* store handles */ }
  }, [id, roleId, store]);

  const loadUsers = useCallback(async () => {
    if (!id || !roleId) return;
    try {
      const res = await store.ListTeamUsersByRole(id, roleId);
      setUsers(res.users?.members ?? []);
    } catch { /* store handles */ }
  }, [id, roleId, store]);

  useEffect(() => {
    if (id) store.GetTeam(id);
    loadRole();
    loadUsers();
  }, [id, roleId, loadRole, loadUsers]);

  const showSuccess = (msg: string) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const handleDelete = async () => {
    if (!id || !roleId || !confirm(t('team.deleteRoleConfirm'))) return;
    try {
      await store.DeleteTeamRole(id, roleId);
      navigate(`/teams/${id}/roles`);
    } catch { /* store handles */ }
  };

  const handleClearPermissions = async () => {
    if (!id || !roleId) return;
    setClearingPerms(true);
    try {
      await store.RemoveTeamPermissionFromRole(id, roleId);
      setConfirmClearPerms(false);
      showSuccess(t('team.permissionsCleared'));
      loadRole();
    } catch { /* store handles */ } finally { setClearingPerms(false); }
  };

  const teamName = getTeamName(store.currentItem) || t('team.title');

  if (store.isLoading && !role) {
    return (
      <div className="space-y-6">
        <div className="h-4 w-48 rounded bg-muted animate-pulse" />
        <div className="flex items-center gap-3 animate-pulse">
          <div className="w-6 h-6 rounded-full bg-muted" />
          <div className="h-7 w-40 rounded bg-muted" />
        </div>
        <div className="rounded-lg border border-border p-6 animate-pulse space-y-3">
          <div className="h-4 w-32 rounded bg-muted" />
          <div className="h-4 w-full rounded bg-muted" />
          <div className="h-4 w-24 rounded bg-muted" />
        </div>
      </div>
    );
  }

  if (!role) {
    return (
      <Alert variant="warning" title={t('common.notFound')}>
        <Link to={`/teams/${id}/roles`} className="text-sm underline text-primary">{t('team.backToRoles')}</Link>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <nav className="text-sm">
        <Link to="/teams" className="text-muted-foreground hover:text-foreground">{t('team.list')}</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to={`/teams/${id}`} className="text-muted-foreground hover:text-foreground">{teamName}</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to={`/teams/${id}/roles`} className="text-muted-foreground hover:text-foreground">{t('team.roles')}</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-foreground">{role.name}</span>
      </nav>

      {store.error != null && (
        <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />
      )}
      {successMessage && (
        <Alert variant="success" dismissible onDismiss={() => setSuccessMessage(null)}>{successMessage}</Alert>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          {role.color && <div className="w-6 h-6 rounded-full" style={{ backgroundColor: role.color }} />}
          <h1 className="text-2xl font-bold text-foreground">{role.name}</h1>
          {role.is_default && <Badge variant="primary" size="sm">{t('team.defaultRole')}</Badge>}
          {role.is_system && <Badge variant="secondary" size="sm">{t('team.systemRole')}</Badge>}
        </div>
        <div className="flex gap-2">
          {canManageRoles && !role.is_system && (
            <>
              <Button variant="secondary" onClick={() => setShowEdit(true)}>
                {t('common.edit')}
              </Button>
              <Button variant="danger" onClick={handleDelete}>{t('common.delete')}</Button>
            </>
          )}
        </div>
      </div>

      {canManageRoles && <RoleFormDialog
        open={showEdit}
        teamId={id!}
        role={role ? ({
          id: roleId,
          name: role.name || '',
          description: role.description || '',
          icon: role.icon || '',
          color: role.color || '',
          priority: role.priority || 0,
          is_default: !!role.is_default,
        } as RoleFormValue) : null}
        onClose={() => setShowEdit(false)}
        onSaved={() => {
          showSuccess(t('team.roleUpdated'));
          loadRole();
        }}
      />}

      {/* Role Info */}
      <Card>
        <CardHeader><CardTitle>{t('team.roleInfo')}</CardTitle></CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">{t('team.roleField.description')}</dt>
              <dd className="text-sm text-foreground col-span-2">{role.description || '--'}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">{t('team.roleField.priority')}</dt>
              <dd className="text-sm text-foreground col-span-2">{role.priority ?? 0}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* Permissions */}
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle>{t('team.permissionsCount', { count: permissions.length })}</CardTitle>
            <CardDescription>{t('team.permissionsDescription')}</CardDescription>
          </div>
          {canManageRoles && !role.is_system && permissions.length > 0 && (
            confirmClearPerms ? (
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-xs text-destructive whitespace-nowrap">{t('team.clearPermissionsConfirm')}</span>
                <Button size="sm" variant="danger" onClick={handleClearPermissions} disabled={clearingPerms}>
                  {clearingPerms ? t('common.loading') : t('common.confirm')}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirmClearPerms(false)}>{t('common.cancel')}</Button>
              </div>
            ) : (
              <Button size="sm" variant="ghost" className="text-destructive flex-shrink-0" onClick={() => setConfirmClearPerms(true)}>
                {t('team.clearPermissions')}
              </Button>
            )
          )}
        </CardHeader>
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>{t('team.permissionCol.action')}</TableHead>
              <TableHead>{t('team.permissionCol.resource')}</TableHead>
              <TableHead>{t('team.permissionCol.scope')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {permissions.length === 0 ? (
              <TableEmpty colSpan={3} message={t('team.noPermissions')} />
            ) : (
              permissions.map((perm: any, i: number) => (
                <TableRow key={perm.id || i}>
                  <TableCell><Badge variant="primary" size="sm">{perm.action}</Badge></TableCell>
                  <TableCell><span className="text-sm">{perm.resource}</span></TableCell>
                  <TableCell><span className="text-sm text-muted-foreground">{perm.scope}</span></TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Users with this role */}
      <Card>
        <CardHeader>
          <CardTitle>{t('team.roleMembersCount', { count: users.length })}</CardTitle>
        </CardHeader>
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>{t('common.user')}</TableHead>
              <TableHead>{t('team.field.status')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.length === 0 ? (
              <TableEmpty colSpan={2} message={t('team.noRoleMembers')} />
            ) : (
              users.map((user: any, i: number) => (
                <TableRow key={user.entity?.id || i}>
                  <TableCell>
                    <span className="text-sm font-medium">{user.actor?.display_name || user.profile?.id || '--'}</span>
                  </TableCell>
                  <TableCell>
                    <Badge variant={user.is_owner ? 'warning' : 'success'} size="sm">
                      {user.is_owner ? t('team.memberRole.owner') : user.is_admin ? t('team.memberRole.admin') : t('team.memberRole.member')}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
});

export default TeamRoleDetailPage;
